import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import SlideDown from 'components/Common/SlideDown'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import TextLink from 'components/Luxkit/TextLink'
import styled from 'styled-components'
import Spacing from 'components/Common/Spacing'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import BedbankPopularFacilities from './Bedbank/BedbankPopularFacilities'
import { clickOfferTruncatedText, clickOfferTruncatedTextOfferType } from 'analytics/optimizely/events'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { isBedbank } from 'lib/offer/offerTypes'

interface Props {
  description: string;
  className?: string;
  additionalDescription?: string;
  promotions?: Array<OfferPackageOptionView>;
  offer: App.AnyOffer;
  hasPromotions?: boolean;
  facilitiesOverride?: string;
  offerPageTruncateText?: boolean;
}

const StyledSlideDown = styled(SlideDown)`
  margin-top: 0;

  > * {
    margin-top: ${rem(16)};
  }
`
const trackReadMoreToggle = (expanded: boolean) => {
  if (expanded) {
    Analytics.track('client-event', clientEvent('read_less', 'pressed', 'offer_page', 'interaction'))
  } else {
    Analytics.track('client-event', clientEvent('read_more', 'pressed', 'offer_page', 'interaction'))
  }
}

export default function OfferDescription(props: Props) {
  const {
    description,
    className,
    additionalDescription,
    offerPageTruncateText,
    offer,
    hasPromotions,
    facilitiesOverride,
  } = props

  const [expanded, toggleReadMore] = useToggle()
  const splitDescription = useMemo(() => description?.length > 0 ? description.split('\n') : [], [description])
  const [shortDescription, ...restOfDescription] = splitDescription
  const expandedDescription = useMemo(() => restOfDescription.length > 1 ? restOfDescription.join('\n') : '', [restOfDescription])
  const isBedbankOffer = isBedbank(offer)

  function toggle() {
    toggleReadMore()
    trackReadMoreToggle(expanded)
    Analytics.track('click-offer-truncated-text', clickOfferTruncatedText())
    Analytics.track('click-offer-truncated-text', clickOfferTruncatedTextOfferType(offer.type))
  }

  if (!description) return null

  return (
    <div className={className}>
      {!offerPageTruncateText && shortDescription && <>
        <MarkdownRender type="compact" content={shortDescription} />
        {expandedDescription && <>
          <StyledSlideDown show={expanded}>
            <MarkdownRender type="compact" content={expandedDescription} />
          </StyledSlideDown>
          <Spacing mt={16}>
            <TextLink endIcon={<DropdownChevron direction={expanded ? 'up' : 'down'}/>} size="medium" onClick={toggle}>
              Read {expanded ? 'less' : 'more'}
            </TextLink>
          </Spacing>
        </>}
      </>}
      {offerPageTruncateText && description &&
        <LargeContentToggle
          maxHeight={isBedbankOffer ? 100 : 56}
          showMoreText="Read more"
          showLessText="Read less"
          onToggle={toggle}
        >
          <VerticalSpacer gap={20}>
            <MarkdownRender content={description} fontSize="medium"/>
            {additionalDescription && <MarkdownRender content={additionalDescription} fontSize="medium"/>}
            {isBedbankOffer && !hasPromotions && !facilitiesOverride && (
              <BedbankPopularFacilities offer={offer} />
            )}
          </VerticalSpacer>
        </LargeContentToggle>
      }
    </div>
  )
}
